import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings as SettingsIcon,
  Table2,
  Package,
  Link2,
  Languages,
  Truck,
  LineChart,
  ClipboardList,
  ChevronRight,
  Container,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FeatureVisibilityEditor } from '@/components/settings/FeatureVisibilityEditor';
import { ProductCatFiltersVisibilityEditor } from '@/components/settings/ProductCatFiltersVisibilityEditor';
import {
  DEFAULT_COLUMN_CONFIG,
  ORDER_TRACKING_OPTIONAL_COLUMNS,
  persistOrderTrackingColumnConfig,
  loadOrderTrackingColumnConfig,
} from '@/modules/orderTracking/config/orderTrackingTableConfig';
import { supplyService, SupplyTableColumnConfig } from '@/services/supplyService';
import {
  FEATURE_VISIBILITY_DEFAULTS,
  featureVisibilityService,
  diffFeatureVisibilityConfig,
  mergeFeatureVisibilityConfig,
  mergeFeatureVisibilityLayers,
  type FeatureVisibilityConfig,
} from '@/services/featureVisibilityService';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { ForecastConfigSettingsCard } from '@/modules/demand-planning/components/ForecastConfigSettingsCard';
import { ForecastMotorSettingsCard } from '@/modules/demand-planning/components/ForecastMotorSettingsCard';
import { SupplyOnDemandSettingsCard } from '@/modules/supply/components/SupplyOnDemandSettingsCard';
import { SupplyProductionScopeSettingsCard } from '@/modules/supply/components/SupplyProductionScopeSettingsCard';
import { SupplyDeliveryDateSettingsCard } from '@/modules/supply/components/SupplyDeliveryDateSettingsCard';
import { SupplyDeliveryNeedDateSettingsCard } from '@/modules/supply/components/SupplyDeliveryNeedDateSettingsCard';
import { SupplyIdealCoverageSettingsCard } from '@/modules/supply/components/SupplyIdealCoverageSettingsCard';
import { SupplyWorkTraySettingsCard } from '@/modules/supply/components/SupplyWorkTraySettingsCard';
import { Link } from 'react-router-dom';

const LANGUAGES = [
  { code: 'es' as const, labelKey: 'settings.languageEs' },
  { code: 'it' as const, labelKey: 'settings.languageIt' },
] as const;

const SUPPLY_COLUMN_OPTIONS: { id: keyof SupplyTableColumnConfig; labelKey: string }[] = [
  { id: 'show_is_validated', labelKey: 'supply.table.validated' },
  { id: 'show_supplier_name', labelKey: 'supply.table.supplier' },
  { id: 'show_stock_actual', labelKey: 'supply.table.currentStock' },
  { id: 'show_stock_en_transito', labelKey: 'supply.table.stockInTransit' },
  { id: 'show_cantidad_sugerida_usuario', labelKey: 'supply.table.userQuantity' },
  { id: 'show_unit', labelKey: 'supply.table.unit' },
  { id: 'show_safety_stock', labelKey: 'supply.table.safetyStock' },
  { id: 'show_monthly_avg_sales', labelKey: 'supply.table.monthlyAvgSales' },
  { id: 'show_venta_media_diaria', labelKey: 'supply.table.dailyAvgSales' },
  { id: 'show_coverage_current', labelKey: 'supply.table.coverageCurrent' },
  { id: 'show_coverage_with_suggested', labelKey: 'supply.table.coverageWithSuggested' },
  { id: 'show_last_purchase_price', labelKey: 'supply.table.suggestedCost' },
  { id: 'show_reorder_point', labelKey: 'supply.table.reorderPoint' },
  { id: 'show_lead_time', labelKey: 'supply.table.leadTime' },
  { id: 'show_family', labelKey: 'supply.table.family' },
  { id: 'show_lifecycle_stage', labelKey: 'supply.table.lifecycle' },
  { id: 'show_abc_category', labelKey: 'supply.table.abcCategory' },
  { id: 'show_supply_type', labelKey: 'supply.table.supplyType' },
];

/** Optional columns hidden unless the org explicitly enables them (aligned with backend). */
const SUPPLY_COLUMNS_DEFAULT_HIDDEN = new Set<string>([
  'show_safety_stock',
  'show_coverage_current',
  'show_coverage_with_suggested',
]);

type SettingsSectionId =
  | 'general'
  | 'catalog'
  | 'supply'
  | 'demand'
  | 'orderTracking';

function SettingsSection({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-6 space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
      </div>
      <div className="space-y-4 [&_.mt-6]:mt-0">{children}</div>
    </section>
  );
}

export function Settings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { isModuleVisible, isRouteEnabled, refresh: refreshFeatureVisibility } = useFeatureVisibility();
  const currentLang = (i18n.language?.split('-')[0] || 'es') as 'es' | 'it';
  const isOrgAdmin = user?.role?.id === 1;
  const canSeeOrderTrackingSettings =
    isModuleVisible('order_tracking', true) && isRouteEnabled('/order-tracking', true);
  const showDemandPlanningSettings = isModuleVisible('demand_planning', true);
  const showProductMaster =
    isModuleVisible('product_master', false) && isRouteEnabled('/settings/products', false);
  const showEquivalences =
    isModuleVisible('equivalences', false) && isRouteEnabled('/settings/equivalences', false);
  const showLogisticsCatalog =
    isModuleVisible('logistics_catalog', false) && isRouteEnabled('/settings/logistics', false);
  const showCatalog = showProductMaster || showEquivalences || showLogisticsCatalog;

  const [activeSection, setActiveSection] = useState<SettingsSectionId>('general');
  const [columnConfig, setColumnConfig] = useState<SupplyTableColumnConfig | null>(null);
  const [loadingColumns, setLoadingColumns] = useState(true);
  const [savingColumns, setSavingColumns] = useState(false);
  const [columnSaveMessage, setColumnSaveMessage] = useState<'success' | 'error' | null>(null);
  const [orderTrackingColumnConfig, setOrderTrackingColumnConfig] = useState<Record<string, boolean>>(DEFAULT_COLUMN_CONFIG);
  const [orderTrackingConfigSaveState, setOrderTrackingConfigSaveState] = useState<'success' | null>(null);
  const [orgDraft, setOrgDraft] = useState<FeatureVisibilityConfig>(() => mergeFeatureVisibilityConfig(null));
  const [loadingOrgFv, setLoadingOrgFv] = useState(false);
  const [orgConfigSaveState, setOrgConfigSaveState] = useState<'success' | 'error' | null>(null);
  const [users, setUsers] = useState<Array<{ id: number; username: string; email: string; name: string; role_id: number; role_name: string }>>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userDraft, setUserDraft] = useState<FeatureVisibilityConfig>(() => mergeFeatureVisibilityConfig(null));
  const [loadingUserFv, setLoadingUserFv] = useState(false);
  const [userConfigSaveState, setUserConfigSaveState] = useState<'success' | 'error' | null>(null);

  const navItems = useMemo(() => {
    const items: Array<{
      id: SettingsSectionId;
      label: string;
      icon: typeof Package;
    }> = [
      { id: 'general', label: t('settings.sections.general'), icon: Languages },
    ];
    if (showCatalog) {
      items.push({ id: 'catalog', label: t('settings.sections.catalog'), icon: Package });
    }
    items.push({ id: 'supply', label: t('settings.sections.supply'), icon: Truck });
    if (showDemandPlanningSettings) {
      items.push({ id: 'demand', label: t('settings.sections.demand'), icon: LineChart });
    }
    if (canSeeOrderTrackingSettings) {
      items.push({ id: 'orderTracking', label: t('settings.sections.orderTracking'), icon: ClipboardList });
    }
    return items;
  }, [
    showCatalog,
    showDemandPlanningSettings,
    canSeeOrderTrackingSettings,
    t,
  ]);

  useEffect(() => {
    if (navItems.length === 0) return;
    if (!navItems.some((item) => item.id === activeSection)) {
      setActiveSection(navItems[0].id);
    }
  }, [navItems, activeSection]);

  const handleLanguageChange = (code: 'es' | 'it') => {
    i18n.changeLanguage(code);
  };

  useEffect(() => {
    let cancelled = false;
    setLoadingColumns(true);
    supplyService
      .getSupplyTableColumnConfig()
      .then((res) => {
        if (!cancelled) setColumnConfig(res.column_config);
      })
      .catch(() => {
        if (!cancelled) setColumnConfig(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingColumns(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setOrderTrackingColumnConfig(loadOrderTrackingColumnConfig());
  }, []);

  useEffect(() => {
    if (!isOrgAdmin) return;
    let cancelled = false;
    const load = async () => {
      setLoadingOrgFv(true);
      try {
        const orgCfg = (await featureVisibilityService.getOrganizationConfig()) as { config_json?: FeatureVisibilityConfig };
        const usersRes = await featureVisibilityService.listOrganizationUsers();
        if (!cancelled) {
          setOrgDraft(mergeFeatureVisibilityConfig(orgCfg.config_json));
          setUsers(usersRes);
        }
      } catch (error) {
        console.error('[Settings] feature visibility load error:', error);
      } finally {
        if (!cancelled) setLoadingOrgFv(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOrgAdmin]);

  useEffect(() => {
    if (!isOrgAdmin || selectedUserId == null) return;
    let cancelled = false;
    const loadUserCfg = async () => {
      setLoadingUserFv(true);
      try {
        const [orgCfg, userCfg] = await Promise.all([
          featureVisibilityService.getOrganizationConfig() as Promise<{
            config_json?: FeatureVisibilityConfig;
          }>,
          featureVisibilityService.getUserConfig(selectedUserId) as Promise<{
            config_json?: FeatureVisibilityConfig;
          }>,
        ]);
        // Estado efectivo (defaults → org → usuario). Evita que el default
        // agent_simulate=false se guarde como override y pise la org.
        if (!cancelled) {
          setUserDraft(
            mergeFeatureVisibilityLayers(
              FEATURE_VISIBILITY_DEFAULTS,
              orgCfg.config_json,
              userCfg.config_json
            )
          );
        }
      } catch (error) {
        console.error('[Settings] feature visibility user load error:', error);
      } finally {
        if (!cancelled) setLoadingUserFv(false);
      }
    };
    void loadUserCfg();
    return () => {
      cancelled = true;
    };
  }, [isOrgAdmin, selectedUserId]);

  const handleColumnToggle = (id: keyof SupplyTableColumnConfig, checked: boolean) => {
    setColumnConfig((prev) => ({
      ...(prev ?? {}),
      [id]: checked,
    }));
    setColumnSaveMessage(null);
  };

  const handleSaveColumns = async () => {
    if (columnConfig == null) return;
    setSavingColumns(true);
    setColumnSaveMessage(null);
    try {
      await supplyService.updateSupplyTableColumnConfig(columnConfig);
      setColumnSaveMessage('success');
    } catch {
      setColumnSaveMessage('error');
    } finally {
      setSavingColumns(false);
    }
  };

  const handleOrderTrackingColumnToggle = (id: string, checked: boolean) => {
    setOrderTrackingColumnConfig((prev) => ({
      ...prev,
      [id]: checked,
    }));
    setOrderTrackingConfigSaveState(null);
  };

  const handleSaveOrderTrackingColumns = () => {
    persistOrderTrackingColumnConfig(orderTrackingColumnConfig);
    setOrderTrackingConfigSaveState('success');
  };

  const handleResetOrderTrackingColumns = () => {
    const defaults = { ...DEFAULT_COLUMN_CONFIG };
    setOrderTrackingColumnConfig(defaults);
    persistOrderTrackingColumnConfig(defaults);
    setOrderTrackingConfigSaveState('success');
  };

  const handleSaveOrgConfig = async () => {
    setOrgConfigSaveState(null);
    try {
      await featureVisibilityService.updateOrganizationConfig(orgDraft);
      setOrgConfigSaveState('success');
      await refreshFeatureVisibility();
    } catch (error) {
      console.error(error);
      setOrgConfigSaveState('error');
    }
  };

  const handleSaveUserConfig = async () => {
    if (selectedUserId == null) return;
    setUserConfigSaveState(null);
    try {
      const orgBase = mergeFeatureVisibilityLayers(FEATURE_VISIBILITY_DEFAULTS, orgDraft);
      const sparseOverride = diffFeatureVisibilityConfig(orgBase, userDraft);
      await featureVisibilityService.updateUserConfig(selectedUserId, sparseOverride);
      setUserConfigSaveState('success');
      if (user?.id === selectedUserId) {
        await refreshFeatureVisibility();
      }
    } catch (error) {
      console.error(error);
      setUserConfigSaveState('error');
    }
  };

  const goToSection = (id: SettingsSectionId) => {
    setActiveSection(id);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {/* Section nav */}
        <nav
          aria-label={t('settings.sections.navLabel')}
          className="shrink-0 lg:sticky lg:top-4 lg:w-56"
        >
          <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:rounded-lg lg:border lg:border-gray-200 lg:bg-white lg:p-2 lg:shadow-sm">
            {navItems.map(({ id, label, icon: Icon }) => {
              const active = activeSection === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => goToSection(id)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors lg:w-full ${
                    active
                      ? 'bg-stocker-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 lg:bg-transparent lg:hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 text-left">{label}</span>
                  {active ? <ChevronRight className="hidden h-4 w-4 lg:block" /> : null}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Section content */}
        <div className="min-w-0 flex-1">
          {activeSection === 'catalog' && showCatalog ? (
            <SettingsSection
              id="catalog"
              title={t('settings.sections.catalog')}
              description={t('settings.sections.catalogHelp')}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                {showProductMaster ? (
                  <Link to="/settings/products" className="group block">
                    <Card className="h-full transition-colors group-hover:border-stocker-primary/40">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between gap-2 text-base">
                          <span className="inline-flex items-center gap-2">
                            <Package className="h-4 w-4 text-stocker-primary" />
                            {t('productMaster.title')}
                          </span>
                          <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-stocker-primary" />
                        </CardTitle>
                        <CardDescription>{t('productMaster.description')}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                ) : null}
                {showEquivalences ? (
                  <Link to="/settings/equivalences" className="group block">
                    <Card className="h-full transition-colors group-hover:border-stocker-primary/40">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between gap-2 text-base">
                          <span className="inline-flex items-center gap-2">
                            <Link2 className="h-4 w-4 text-stocker-primary" />
                            {t('equivalences.title')}
                          </span>
                          <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-stocker-primary" />
                        </CardTitle>
                        <CardDescription>{t('equivalences.description')}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                ) : null}
                {showLogisticsCatalog ? (
                  <Link to="/settings/logistics" className="group block">
                    <Card className="h-full transition-colors group-hover:border-stocker-primary/40">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between gap-2 text-base">
                          <span className="inline-flex items-center gap-2">
                            <Container className="h-4 w-4 text-stocker-primary" />
                            {t('logisticsCatalog.title')}
                          </span>
                          <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-stocker-primary" />
                        </CardTitle>
                        <CardDescription>{t('logisticsCatalog.description')}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                ) : null}
              </div>
            </SettingsSection>
          ) : null}

          {activeSection === 'general' ? (
            <SettingsSection
              id="general"
              title={t('settings.sections.general')}
              description={t('settings.sections.generalHelp')}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <SettingsIcon className="h-5 w-5 text-stocker-primary" />
                    {t('settings.language')}
                  </CardTitle>
                  <CardDescription>{t('settings.languageDescription')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex max-w-md gap-0 rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {LANGUAGES.map(({ code, labelKey }) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => handleLanguageChange(code)}
                        className={`flex-1 rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                          currentLang === code
                            ? 'border border-gray-200 bg-white text-stocker-primary shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {t(labelKey)}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {isOrgAdmin ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{t('settings.featureVisibility.orgTitle')}</CardTitle>
                      <CardDescription>{t('settings.featureVisibility.orgDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingOrgFv ? (
                        <p className="text-sm text-gray-500">{t('settings.featureVisibility.loading')}</p>
                      ) : (
                        <>
                          <ProductCatFiltersVisibilityEditor value={orgDraft} onChange={setOrgDraft} />
                          <div className="mt-6 border-t border-gray-100 pt-6">
                            <FeatureVisibilityEditor value={orgDraft} onChange={setOrgDraft} />
                          </div>
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={handleSaveOrgConfig}
                              className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:bg-stocker-primary/90"
                            >
                              {t('settings.featureVisibility.saveOrg')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setOrgDraft(mergeFeatureVisibilityConfig(null));
                                setOrgConfigSaveState(null);
                              }}
                              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              {t('settings.featureVisibility.resetDefaults')}
                            </button>
                            {orgConfigSaveState === 'success' && (
                              <span className="text-sm text-green-600">{t('settings.saveSuccess')}</span>
                            )}
                            {orgConfigSaveState === 'error' && (
                              <span className="text-sm text-red-600">{t('settings.saveError')}</span>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{t('settings.featureVisibility.userTitle')}</CardTitle>
                      <CardDescription>{t('settings.featureVisibility.userDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <select
                          value={selectedUserId ?? ''}
                          onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                          <option value="">{t('settings.featureVisibility.selectUser')}</option>
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.username})
                              {u.role_name ? ` — ${u.role_name}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedUserId == null ? (
                        <p className="text-sm text-gray-500">{t('settings.featureVisibility.selectUserHint')}</p>
                      ) : loadingUserFv ? (
                        <p className="text-sm text-gray-500">{t('settings.featureVisibility.loading')}</p>
                      ) : (
                        <>
                          <FeatureVisibilityEditor value={userDraft} onChange={setUserDraft} />
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={handleSaveUserConfig}
                              className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:bg-stocker-primary/90"
                            >
                              {t('settings.featureVisibility.saveUser')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setUserDraft(
                                  mergeFeatureVisibilityLayers(FEATURE_VISIBILITY_DEFAULTS, orgDraft)
                                );
                                setUserConfigSaveState(null);
                              }}
                              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              {t('settings.featureVisibility.resetDefaults')}
                            </button>
                            {userConfigSaveState === 'success' && (
                              <span className="text-sm text-green-600">{t('settings.saveSuccess')}</span>
                            )}
                            {userConfigSaveState === 'error' && (
                              <span className="text-sm text-red-600">{t('settings.saveError')}</span>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : null}
            </SettingsSection>
          ) : null}

          {activeSection === 'supply' ? (
            <SettingsSection
              id="supply"
              title={t('settings.sections.supply')}
              description={t('settings.sections.supplyHelp')}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Table2 className="h-5 w-5 text-stocker-primary" />
                    {t('settings.supplyTableColumns')}
                  </CardTitle>
                  <CardDescription>{t('settings.supplyTableColumnsDescription')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingColumns ? (
                    <p className="text-sm text-gray-500">{t('common.loading')}</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {SUPPLY_COLUMN_OPTIONS.map(({ id, labelKey }) => (
                          <label
                            key={id}
                            className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={
                                columnConfig?.[id] !== undefined
                                  ? columnConfig[id] !== false
                                  : !SUPPLY_COLUMNS_DEFAULT_HIDDEN.has(id)
                              }
                              onChange={(e) => handleColumnToggle(id, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                            />
                            {t(labelKey)}
                          </label>
                        ))}
                      </div>
                      <div className="mt-4 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleSaveColumns}
                          disabled={savingColumns || columnConfig == null}
                          className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:bg-stocker-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {savingColumns ? t('common.loading') : t('settings.supplyTableColumnsSave')}
                        </button>
                        {columnSaveMessage === 'success' && (
                          <span className="text-sm text-green-600">{t('settings.saveSuccess')}</span>
                        )}
                        {columnSaveMessage === 'error' && (
                          <span className="text-sm text-red-600">{t('settings.saveError')}</span>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <SupplyOnDemandSettingsCard />
              <SupplyProductionScopeSettingsCard />
              <SupplyWorkTraySettingsCard />
              <SupplyDeliveryDateSettingsCard />
              <SupplyDeliveryNeedDateSettingsCard />
              <SupplyIdealCoverageSettingsCard />
            </SettingsSection>
          ) : null}

          {activeSection === 'demand' && showDemandPlanningSettings ? (
            <SettingsSection
              id="demand"
              title={t('settings.sections.demand')}
              description={t('settings.sections.demandHelp')}
            >
              <ForecastMotorSettingsCard />
              <ForecastConfigSettingsCard />
            </SettingsSection>
          ) : null}

          {activeSection === 'orderTracking' && canSeeOrderTrackingSettings ? (
            <SettingsSection
              id="orderTracking"
              title={t('settings.sections.orderTracking')}
              description={t('settings.sections.orderTrackingHelp')}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Table2 className="h-5 w-5 text-stocker-primary" />
                    {t('settings.orderTrackingTableColumns')}
                  </CardTitle>
                  <CardDescription>{t('settings.orderTrackingTableColumnsDescription')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {ORDER_TRACKING_OPTIONAL_COLUMNS.map(({ id, labelKey }) => (
                      <label key={id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={orderTrackingColumnConfig[id] !== false}
                          onChange={(e) => handleOrderTrackingColumnToggle(id, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                        />
                        {t(labelKey)}
                      </label>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleSaveOrderTrackingColumns}
                      className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:bg-stocker-primary/90"
                    >
                      {t('settings.orderTrackingTableColumnsSave')}
                    </button>
                    <button
                      type="button"
                      onClick={handleResetOrderTrackingColumns}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t('settings.orderTrackingTableColumnsReset')}
                    </button>
                    {orderTrackingConfigSaveState === 'success' && (
                      <span className="text-sm text-green-600">{t('settings.saveSuccess')}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </SettingsSection>
          ) : null}
        </div>
      </div>
    </div>
  );
}
